import {
  AppstoreOutlined,
  BarChartOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownOutlined,
  NumberOutlined,
  TableOutlined,
  UnorderedListOutlined,
  UpOutlined
} from "@ant-design/icons"
import {
  Button,
  Empty,
  Flex,
  Popconfirm,
  Radio,
  Select,
  Typography
} from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { TextInput } from "../../../../components/Input/TextInput.tsx"
import { brand, neutral } from "../../../../components/widgets/tokens.ts"
import type { ChartVisualisation } from "../../../../components/widgets/types.ts"
import { filterByLabelOrValue } from "../../../DashboardPage/components/Portfolio/FilterBar.tsx"
import { FILTER_DIMENSION_LABEL_KEYS } from "../../helpers/reportFilterSummary.ts"
import { createEmptyReportWidgetFilters } from "../../helpers/reportWidgetFilters.ts"
import { useReportBuildActions } from "../../hooks/useReportBuildActions.ts"
import { useReportFilterOptions } from "../../hooks/useReportFilterOptions.ts"
import {
  REPORT_COMPARE_OVERRIDES,
  REPORT_FILTER_DIMENSIONS,
  type ReportCompareOverride,
  type ReportFilterDimension,
  WIDGET_SIZE_PRESETS
} from "../../types/canvas.types.ts"

const { Text } = Typography

const COMPARE_OPTION_LABEL_KEYS: Record<ReportCompareOverride, string> = {
  inherit: "reports.widgetConfig.compareOptions.inherit",
  none: "reports.widgetConfig.compareOptions.none",
  previous_period: "reports.widgetConfig.compareOptions.previousPeriod",
  same_period_last_year: "reports.widgetConfig.compareOptions.samePeriodLastYear"
}

const KIND_ICON: Record<string, React.ReactNode> = {
  kpi: <NumberOutlined />,
  chart: <BarChartOutlined />,
  table: <TableOutlined />,
  list: <UnorderedListOutlined />
}

const KIND_KEYS = new Set([
  "kpi",
  "chart",
  "table",
  "list",
  "progress",
  "feed",
  "alert"
])

const Section = ({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) => (
  <Flex vertical gap={6}>
    <Text strong className="text-[13px]">
      {label}
    </Text>
    {children}
  </Flex>
)

/**
 * The selected widget's own settings. It offers only what the widget supports
 * — a visualisation switch only where the resolved chart has one — and the
 * report's default filters are not here: they belong to the Filters step.
 */
export const ReportWidgetSettingsPanel = () => {
  const { t } = useTranslation()
  const { options, loading } = useReportFilterOptions()
  const {
    selectedWidget,
    selectedSpec,
    visualisationOptions,
    selectWidget,
    removeWidget,
    changeTitle,
    changeSize,
    changeFilters,
    changeCompare,
    changeVisualisation
  } = useReportBuildActions()

  const [titleDraft, setTitleDraft] = useState("")
  const [filtersOpen, setFiltersOpen] = useState(false)

  useEffect(() => {
    setTitleDraft(selectedWidget?.title ?? "")
  }, [selectedWidget?.id, selectedWidget?.title])

  const panelClass =
    "flex w-[320px] flex-none flex-col gap-4 overflow-auto rounded-xl border border-solid bg-white p-4"

  if (!selectedWidget) {
    return (
      <aside className={panelClass} style={{ borderColor: neutral.border }}>
        <Text strong>{t("reports.settings.title")}</Text>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("reports.settings.noSelection")}
        />
      </aside>
    )
  }

  const widget = selectedWidget
  const filters = widget.filters ?? createEmptyReportWidgetFilters()
  const activeFilterCount = REPORT_FILTER_DIMENSIONS.filter(
    (dimension) => (filters[dimension]?.length ?? 0) > 0
  ).length
  const kind = selectedSpec?.type ?? "widget"
  const kindLabel = t(
    `reports.settings.kinds.${KIND_KEYS.has(kind) ? kind : "widget"}`
  )

  const commitTitle = () => {
    if (titleDraft.trim() === (widget.title ?? "")) return
    changeTitle(widget.id, titleDraft)
  }

  const handleFilterChange =
    (dimension: ReportFilterDimension) => (value: string[]) =>
      changeFilters(widget.id, { ...filters, [dimension]: value })

  return (
    <aside
      aria-label={t("reports.settings.title")}
      className={panelClass}
      style={{ borderColor: neutral.border }}
    >
      <Flex align="center" justify="space-between">
        <Text strong className="text-base">
          {t("reports.settings.title")}
        </Text>
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined aria-hidden />}
          aria-label={t("reports.settings.close")}
          onClick={() => selectWidget(null)}
        />
      </Flex>

      <Flex vertical gap={6}>
        <Text type="secondary" className="text-xs">
          {t("reports.settings.selectedWidget")}
        </Text>
        <Flex
          align="center"
          gap="small"
          className="rounded-lg border border-solid p-2"
          style={{ borderColor: neutral.border }}
        >
          <span
            aria-hidden
            className="flex size-9 shrink-0 items-center justify-center rounded-md text-lg"
            style={{ background: "rgba(42,125,140,.10)", color: brand.teal }}
          >
            {KIND_ICON[kind] ?? <AppstoreOutlined />}
          </span>
          <Flex vertical className="min-w-0">
            <Text strong ellipsis>
              {widget.title || selectedSpec?.title}
            </Text>
            <Text type="secondary" className="text-xs">
              {kindLabel}
            </Text>
          </Flex>
        </Flex>
      </Flex>

      <Section label={t("reports.widgetConfig.fields.title")}>
        <TextInput
          value={titleDraft}
          aria-label={t("reports.widgetConfig.fields.title")}
          placeholder={selectedSpec?.title ?? ""}
          onChange={(e) => setTitleDraft(e.target.value)}
          onBlur={commitTitle}
          onPressEnter={commitTitle}
        />
      </Section>

      {visualisationOptions && (
        <Section label={t("reports.widgetConfig.fields.visualisation")}>
          <Select<ChartVisualisation>
            allowClear
            aria-label={t("reports.widgetConfig.fields.visualisation")}
            value={widget.visualisation}
            placeholder={t("reports.widgetConfig.visualisationOptions.default")}
            onChange={(value) =>
              changeVisualisation(widget.id, value ?? undefined)
            }
            options={visualisationOptions.map((option) => ({
              label: t(`reports.widgetConfig.visualisationOptions.${option}`),
              value: option
            }))}
          />
        </Section>
      )}

      <Section label={t("reports.widgetConfig.fields.size")}>
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          size="small"
          value={widget.size}
          onChange={(e) => changeSize(widget.id, e.target.value)}
          options={WIDGET_SIZE_PRESETS.map((preset) => ({
            label: t(`reports.widgetConfig.sizeOptions.${preset}`),
            value: preset
          }))}
        />
      </Section>

      <Section label={t("reports.widgetConfig.fields.compare")}>
        <Select<ReportCompareOverride>
          aria-label={t("reports.widgetConfig.fields.compare")}
          value={widget.compare ?? "inherit"}
          onChange={(value) => changeCompare(widget.id, value)}
          options={REPORT_COMPARE_OVERRIDES.map((option) => ({
            label: t(COMPARE_OPTION_LABEL_KEYS[option]),
            value: option
          }))}
        />
      </Section>

      <section
        className="rounded-lg border border-solid"
        style={{ borderColor: neutral.border }}
      >
        <button
          type="button"
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen((open) => !open)}
          className="flex w-full cursor-pointer items-center justify-between gap-2 border-0 bg-transparent px-3 py-2 text-left"
        >
          <Flex vertical>
            <Text strong className="text-[13px]">
              {t("reports.widgetConfig.filters.title")}
            </Text>
            <Text type="secondary" className="text-xs">
              {activeFilterCount
                ? t("reports.widgetConfig.filters.activeCount", {
                    count: activeFilterCount
                  })
                : t("reports.widgetConfig.filters.none")}
            </Text>
          </Flex>
          <span aria-hidden style={{ color: neutral.muted }}>
            {filtersOpen ? <UpOutlined /> : <DownOutlined />}
          </span>
        </button>
        {filtersOpen && (
          <Flex vertical gap="small" className="px-3 pb-3">
            {REPORT_FILTER_DIMENSIONS.map((dimension) => (
              <Select<string[]>
                key={dimension}
                mode="multiple"
                allowClear
                showSearch
                loading={loading}
                maxTagCount="responsive"
                filterOption={filterByLabelOrValue}
                aria-label={t(FILTER_DIMENSION_LABEL_KEYS[dimension])}
                placeholder={t(FILTER_DIMENSION_LABEL_KEYS[dimension])}
                value={filters[dimension] ?? []}
                options={options[dimension]}
                onChange={handleFilterChange(dimension)}
              />
            ))}
            <Button
              size="small"
              disabled={activeFilterCount === 0}
              onClick={() => changeFilters(widget.id, undefined)}
            >
              {t("reports.widgetConfig.filters.clear")}
            </Button>
          </Flex>
        )}
      </section>

      <Popconfirm
        title={t("reports.settings.removeConfirm")}
        okText={t("reports.settings.removeWidget")}
        okButtonProps={{ danger: true }}
        cancelText={t("reports.cancel")}
        onConfirm={() => removeWidget(widget.id)}
      >
        <Button danger icon={<DeleteOutlined aria-hidden />}>
          {t("reports.settings.removeWidget")}
        </Button>
      </Popconfirm>
    </aside>
  )
}
